import { covertToImageDataURL } from '@user-center/services/common';
import { isEmpty } from '@intelligence/v3/utils/common';
import { getOptionText } from '@intelligence/utils/topchartsUtils';
import { getSimpleFileName, getFileFromBase64 } from '@user-center/utils';
import { ReportState } from '@user-center/enums/reports';
import { serializeReportOriginalFilePaths } from '@home/utils/reportPreview';
import {
  GAME_RESEARCH_CATEGORY_VALUE,
  LOCATION_OPTIONS,
  DEFAULT_REPORT,
  DEFAULT_REPORT_REQ,
  DEFAULT_FORM_MODEL,
  FIELD_COMMON_ERR_MSG,
  PERMISSION_ALL_VALUE,
} from '../constants';

import type { CascaderOption } from 'tea-component';
import type { ILocationOption } from '../components/LocationSelect/types';

const IGNORE_AVAILABLE_KEYS = new Set<string>(['download_enable', 'watermark_enable']);

const IGNORE_VALIDATE_KEYS = new Set<string>([
  'file',
  'file_en',
  'permission',
  'collaborators',
  'download_enable',
  'watermark_enable',
]);

const FILE_VALIDATE_KEYS = new Set<string>(['cover', 'creator_avatar']);

const isFileValueFilled = (value?: { key?: string; url?: string; file?: File | null; }): boolean => {
  return Boolean(value?.key || value?.url || value?.file);
};

const isFileValueValid = (value?: { key?: string; url?: string | null; }): boolean => {
  return Boolean(value?.key && value?.url);
};

export const getIsAvailable = (values?: IReportFormModel | null): boolean => {
  if (!values) return false;

  return Object.entries(values).some(([key, value]) => {
    if (IGNORE_AVAILABLE_KEYS.has(key)) return false;
    if (FILE_VALIDATE_KEYS.has(key)) return isFileValueFilled(value as any);
    return !isEmpty(value);
  });
};

export const handleValidate = (values?: IReportFormModel | null): IRecord<string> => {
  if (!values) return {};

  const errors: IRecord<string> = {};
  const entries = Object.entries(values);

  /**
   * 校验报告文件，中文报告、英文报告至少上传一种
   */
  const { file, file_en: fileEN } = values || {};
  const { key: fileKey } = file || {};
  const { key: fileENKey } = fileEN || {};
  const isFileValid = !!fileKey;
  const isFileENValid = !!fileENKey;
  if (!isFileValid && !isFileENValid) {
    if (!isFileValid) {
      errors.file = FIELD_COMMON_ERR_MSG;
    }

    if (!isFileENValid) {
      errors.file_en = FIELD_COMMON_ERR_MSG;
    }
  }

  for (const entry of entries) {
    const [key, value] = entry;
    // 跳过忽略校验字段字段
    if (IGNORE_VALIDATE_KEYS.has(key)) continue;
    // 报告封面、创作者头像
    if (FILE_VALIDATE_KEYS.has(key)) {
      if (!isFileValueValid(value as any)) {
        errors[key] = FIELD_COMMON_ERR_MSG;
      }
      continue;
    }
    if (isEmpty(value)) {
      errors[key] = FIELD_COMMON_ERR_MSG;
    }
  }

  const authorOpenIds = [
    values.creator_name_en,
    ...(values.collaborators || []).map((collaborator) => collaborator.open_id),
  ].filter(Boolean);
  if (
    (values.collaborators || []).some((collaborator) => !collaborator.open_id || !collaborator.creator_department_en)
    || authorOpenIds.length !== new Set(authorOpenIds).size
  ) {
    errors.collaborators = FIELD_COMMON_ERR_MSG;
  }

  return errors;
};

export const getDefaultReportCoverFileName = (fileName?: string): string => {
  if (isEmpty(fileName)) return '';
  return `cover-${getSimpleFileName(fileName)}.jpeg`;
};

export const getFileNameFromPath = (path?: string): string => {
  if (isEmpty(path)) return '';
  const index = path!.lastIndexOf('/');
  return index === -1 ? '' : decodeURI(path!.substring(index + 1));
};

export const getImageFileDataCrossOriginPossibility = async (params: {
  url: string;
  getFileKey: () => string;
  getFileName: () => string;
}): Promise<{
  fileKey: string;
  fileName: string;
  fileUrl: string;
  file: File | null;
}> => {
  const { url, getFileName, getFileKey } = params || {};

  let fileKey = getFileKey();
  let fileName = getFileName();
  let fileUrl = '';
  let file: File | null;

  try {
    const res = await covertToImageDataURL({ imgs: [url] });
    fileUrl = res?.data?.base64?.[0] || '';
    file = await getFileFromBase64(fileUrl, fileName);
  } catch (error) {
    console.error(error);
    fileKey = '';
    fileName = '';
    fileUrl = '';
    file = null;
  }

  return { fileKey, fileName, fileUrl, file };
};

export const formatPermissionOptions = (locale: string, classifiedOrganizations?: any[] | null): CascaderOption[] => {
  if (isEmpty(classifiedOrganizations)) return [];

  return classifiedOrganizations!.map((classifiedOrganization) => {
    const classificationEN = classifiedOrganization?.classification_en || '';
    const classificationCN = classifiedOrganization?.classification_cn || '';
    const classifiedOrganizationText = getOptionText(locale, {
      value: classificationCN,
      text: classificationCN,
      textEN: classificationEN,
      textCN: classificationCN,
    });
    const subOptions = (() => {
      const groups = classifiedOrganization?.group_data;
      if (isEmpty(groups)) return null;

      return groups!.map((group: any) => {
        const groupId: number = group?.group_id ?? Number.NaN;
        const groupName = group?.group_name || '';
        return {
          id: groupId,
          value: Number.isNaN(groupId) ? '' : String(groupId),
          label: groupName,
        };
      });
    })();

    return {
      id: classificationCN,
      value: classificationCN,
      label: classifiedOrganizationText,
      children: subOptions,
    };
  });
};

export const getSegmentText = (locale: string, system: string, source: string, subSource?: string): string => {
  const option = LOCATION_OPTIONS.find((item) => item?.value === system);
  const subOption = option?.children?.find((item) => item?.value === source);
  const subSubOption = subOption?.children?.find((item: any) => item?.value === subSource);

  const subOptionTextEN = subOption?.text || '';
  const subOptionTextCN = subOption?.textCN || '';

  let text = getOptionText(locale, {
    value: subOption?.value || source || '',
    text: subOptionTextEN,
    textEN: subOptionTextEN,
    textCN: subOptionTextCN,
  });

  if (subSource && subSubOption) {
    const subSubOptionTextEN = subSubOption?.text || '';
    const subSubOptionTextCN = subSubOption?.textCN || '';
    const l3Text = getOptionText(locale, {
      value: subSubOption?.value || subSource || '',
      text: subSubOptionTextEN,
      textEN: subSubOptionTextEN,
      textCN: subSubOptionTextCN,
    });
    text += ` > ${l3Text}`;
  }

  return text;
};

export const getLocationText = (locale: string, system: string, source: string, subSource?: string): string => {
  const option = LOCATION_OPTIONS.find((item) => item?.value === system);
  const optionTextEN = option?.text || '';
  const optionTextCN = option?.textCN || '';
  const systemText = getOptionText(locale, {
    value: optionTextEN || system || '',
    text: optionTextEN,
    textEN: optionTextEN,
    textCN: optionTextCN,
  });
  const segmentText = getSegmentText(locale, system, source, subSource);

  return `${systemText}: ${segmentText}`;
};

const isPermissionAllSelected = (permissions?: string[][] | null): boolean => {
  if (isEmpty(permissions) || permissions!.length !== 1) return false;
  const [path] = permissions!;
  return Array.isArray(path) && path.length === 1 && path[0] === PERMISSION_ALL_VALUE;
};

export const formatPermissions = (
  permissions?: IReportPermission[] | null,
  permissionOptions?: CascaderOption[] | null,
): string[][] => {
  // 接口空权限表示不限（全部），回填为 Cascader 的「全部」选中态
  if (isEmpty(permissions)) return [[PERMISSION_ALL_VALUE]];

  return permissions!.map((item) => {
    const permissionType = item?.permission_type || '';
    const permissionName = item?.permission_name || '';
    const groupId = item?.group_id || '';

    switch (permissionType) {
      case 'classification': {
        return !permissionName ? [] : [permissionName];
      }
      case 'group_id': {
        const permissionOption = permissionOptions?.find((item) => {
          // @ts-ignore
          const subPermissionOption = item?.children?.find((subItem) => subItem?.value === String(groupId));
          return !!subPermissionOption;
        });
        const permissionOptionName = permissionOption?.value;
        return !permissionOptionName || isEmpty(groupId) ? [] : [permissionOptionName, String(groupId)];
      }
      default: {
        return [];
      }
    }
  });
};

const convertPermissions = (
  permissions?: string[][] | null,
  options?: CascaderOption[] | null,
): IReportPermission[] => {
  // 未选择或选择「全部」均按不限权限提交
  if (isEmpty(permissions) || isPermissionAllSelected(permissions)) return [];

  return permissions!.map((permission?: string[] | null) => {
    const [code, subCode] = permission || [];
    const option = options?.find((item) => item?.value === code);
    // @ts-ignore
    const subOptions = option?.children as CascaderOption[] | undefined;
    const hasSubOptions = Array.isArray(subOptions) && subOptions.length > 0;

    if (!hasSubOptions || !subCode) return { permission_type: 'classification', permission_name: code };

    const subOption = subOptions.find((item) => item?.value === subCode);

    return {
      permission_type: 'group_id',
      permission_name: (subOption?.label as string) || '',
      group_id: Number.parseInt(subCode, 10),
    };
  });
};

export const formatReport = (report?: IReportReq | null): IReport => {
  const {
    file_name: fileName,
    file_name_en: enFileName,
    wartermark_enable: watermarkEnable,
    ...restReport
  } = report || DEFAULT_REPORT_REQ;

  return {
    ...restReport,
    file_name: decodeURI(fileName || ''),
    file_name_en: decodeURI(enFileName || ''),
    watermark_enable: watermarkEnable ?? 0,
  };
};

export const formatReportReq = (report?: IReport | null): IReportReq => {
  const { watermark_enable: watermarkEnable, ...restReport } = report || DEFAULT_REPORT;
  return {
    ...restReport,
    // 由于页面交互限制，创作者部门只有一个输入框，所以无法区分中、英文，所以这里创作者部门中、英文两个字段 暂时统一传一个值即可
    creator_department: restReport?.creator_department_en || '',
    wartermark_enable: watermarkEnable,
  };
};

export const buildReport = (
  report?: IReportFormModel | null,
  params?: {
    permissionOptions?: any[] | null;
    reportHelperInfo?: any;
  },
): IReportPartial => {
  const {
    file,
    file_en: fileEN,
    cover,
    location,
    permission,
    creator_avatar: creatorAvatar,
    collaborators,
    download_enable: downloadEnable,
    watermark_enable: watermarkEnable,
    ...restReport
  } = report || DEFAULT_FORM_MODEL;

  const { permissionOptions, reportHelperInfo } = params || {};
  return {
    ...restReport,
    system: location?.[0] || '',
    source: location?.[1] || '',
    sub_source: location?.[2] || '',
    report_language: reportHelperInfo?.report_language || '',
    report_language_en: reportHelperInfo?.report_language_en || '',
    file_cn_language: reportHelperInfo?.file_cn_language || '',
    file_en_language: reportHelperInfo?.file_en_language || '',
    file_name: file?.key || '',
    file_name_en: fileEN?.key || '',
    original_file_name: serializeReportOriginalFilePaths({
      filePathCN: file?.originalKey || '',
      filePathEN: fileEN?.originalKey || '',
    }),
    cover: cover?.url || '',
    creator_name: reportHelperInfo?.creator_name || '',
    creator_head_picture: creatorAvatar?.url || '',
    creator_department: reportHelperInfo?.creator_department || '',
    collaborators: (collaborators || []).map((collaborator, index) => {
      const collaboratorHelperInfo = reportHelperInfo?.collaborators?.[index] || {};

      return {
        open_id: collaborator.open_id || '',
        creator_name: collaboratorHelperInfo.creator_name || collaborator.creator_name || '',
        creator_name_en: collaborator.creator_name_en || collaborator.open_id || '',
        creator_department: collaboratorHelperInfo.creator_department || collaborator.creator_department || '',
        creator_department_en: collaborator.creator_department_en || '',
        email: collaborator.email || '',
        creator_head_picture: collaborator.creator_avatar?.url || '',
      };
    }),
    permission: convertPermissions(permission, permissionOptions),
    download_enable: downloadEnable ? 1 : 0,
    watermark_enable: watermarkEnable ? 1 : 0,
    state: ReportState.UnPublished,
  };
};

// 报告语言名（英文报告的中文名）
const DEFAULT_EN_REPORT_NAME_CN = '英文报告';
// 报告语言名（中文报告的中文名）
const DEFAULT_CN_REPORT_NAME_CN = '中文报告';
/**
 * 获取中文报告、英文报告的 语言名（中文）
 * @param isENReport 是否为英文报告
 */
export const getDefaultReportLanguageNameCN = (isENReport: boolean): string => {
  return isENReport ? DEFAULT_EN_REPORT_NAME_CN : DEFAULT_CN_REPORT_NAME_CN;
};

// 报告语言名（英文报告的英文名）
const DEFAULT_EN_REPORT_NAME_EN = 'English Report';
// 报告语言名（中文报告的英文名）
const DEFAULT_CN_REPORT_NAME_EN = 'Chinese Report';
/**
 * 获取中文报告、英文报告的 语言名（英文）
 * @param isENReport 是否为英文报告
 */
export const getDefaultReportLanguageNameEN = (isENReport: boolean): string => {
  return isENReport ? DEFAULT_EN_REPORT_NAME_EN : DEFAULT_CN_REPORT_NAME_EN;
};

/**
 * 根据当前选中的一级分类值，生成带 disabled 字段的 location options
 * 禁用规则：
 * 1. 当前选中 Game Research Report 时，禁用其他所有一级分类
 * 2. 当前选中非 Game Research Report 时，禁用 Game Research Report
 * @param options 原始选项
 * @param currentL1Value 当前选中的一级分类值
 */
export const getLocationOptionsWithDisabled = (
  options: ILocationOption[],
  currentL1Value: string | undefined,
): ILocationOption[] => {
  if (!currentL1Value) {
    return options;
  }

  const isGameResearch = currentL1Value === GAME_RESEARCH_CATEGORY_VALUE;

  return options.map((opt) => {
    return {
      ...opt,
      disabled: isGameResearch
        ? opt.value !== GAME_RESEARCH_CATEGORY_VALUE
        : opt.value === GAME_RESEARCH_CATEGORY_VALUE,
    };
  });
};
